<template>
    <el-drawer
        :model-value="modelValue"
        direction="rtl"
        :size="'min(900px, 90vw)'"
        :modal="true"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :before-close="handleDrawerBeforeClose"
        :with-header="false"
        append-to-body
        class="followup-drawer"
    >
        <section class="followup-layout">
            <header class="followup-header">
                <div class="followup-heading">
                    <strong>{{ summary ? `${summaryValue('serialNo')}的回访记录` : '回访记录' }}</strong>
                    <el-button text circle aria-label="关闭回访记录" @click="requestClose">
                        <el-icon><CloseBold /></el-icon>
                    </el-button>
                </div>
                <div v-if="summary" class="followup-summary">
                    <div class="followup-info-section followup-block">
                        <div class="followup-info-title">药方信息</div>
                        <div class="followup-info-grid">
                            <div><span>药方号：</span><strong>{{ summaryValue('serialNo') }}</strong></div>
                            <div><span>开方时间：</span><strong>{{ formatDateTime(summaryValue('createTime')) }}</strong></div>
                            <div><span>诊断医生：</span><strong>{{ summaryValue('doctorName') }}</strong></div>
                        </div>
                    </div>
                    <div class="followup-info-section followup-block">
                        <div class="followup-info-title">诊断信息</div>
                        <div class="followup-info-grid followup-info-grid-wide">
                            <div><span>主诉：</span><p>{{ summaryValue('mainSuit') }}</p></div>
                            <div><span>诊断：</span><p>{{ summaryValue('clinicalDiagnosis') }}</p></div>
                            <div><span>既往史：</span><p>{{ summaryValue('patientMedicalHistory') }}</p></div>
                            <div><span>现病史：</span><p>{{ summaryValue('nowMedicalHistory') }}</p></div>
                            <div><span>过敏史：</span><p>{{ summaryValue('patientOperationHistory') }}</p></div>
                            <div><span>检查记录：</span><p>{{ summaryValue('inspectionRecord') }}</p></div>
                        </div>
                    </div>
                </div>
            </header>

            <div v-if="!loading && summary" class="followup-fixed-toolbar">
                <div class="followup-records-toolbar">
                    <div class="followup-section-title">
                        <span>回访记录</span>
                        <el-tag v-if="followups.length" size="small" effect="plain">{{ followups.length }}条</el-tag>
                    </div>
                    <div class="followup-toolbar-actions">
                        <el-button
                            size="small"
                            circle
                            :loading="loading"
                            :disabled="saving"
                            title="刷新回访记录"
                            aria-label="刷新回访记录"
                            @click="refreshFollowups"
                        >
                            <el-icon><Refresh /></el-icon>
                        </el-button>
                        <el-button
                            size="small"
                            :title="followupSortOrder === 'desc' ? '当前从近到远，点击切换为从远到近' : '当前从远到近，点击切换为从近到远'"
                            :aria-label="followupSortOrder === 'desc' ? '当前从近到远，点击切换为从远到近' : '当前从远到近，点击切换为从近到远'"
                            @click="followupSortOrder = followupSortOrder === 'desc' ? 'asc' : 'desc'"
                        >
                            <span class="followup-sort-label">排序</span>
                            <span class="followup-sort-icon" :class="{ 'is-ascending': followupSortOrder === 'asc' }" aria-hidden="true">
                                <el-icon><Sort /></el-icon>
                            </span>
                        </el-button>
                        <el-button type="primary" size="small" :disabled="isAdding || editingFollowupId !== null || saving" @click="openAdd">新增回访记录</el-button>
                    </div>
                </div>
            </div>

            <main class="followup-timeline" v-loading="loading">
                <template v-if="loadError">
                    <el-result icon="error" title="加载失败" :sub-title="loadError">
                        <template #extra><el-button type="primary" @click="loadData">重试</el-button></template>
                    </el-result>
                </template>
                <template v-else-if="!loading && summary">
                    <div class="followup-records-section">
                        <div v-if="followups.length === 0 && !isAdding" class="followup-empty-state">
                            <el-empty description="暂无回访记录" :image-size="80" />
                        </div>
                    </div>
                    <div class="followup-record-list">
                    <div v-for="record in sortedFollowups" :key="record.followupId" class="followup-entry">
                        <div class="followup-time">
                            <span class="followup-time-icon" aria-hidden="true"><el-icon><Clock /></el-icon></span>
                            <span>{{ formatDateTime(record.createTime) }}</span>
                        </div>
                        <div class="followup-card">

                            <template v-if="editingFollowupId === record.followupId">
                                <el-form label-position="top" :model="editDraft">
                                    <el-form-item label="回访记录内容" required>
                                        <el-input v-model="editDraft.followupContent" type="textarea" :rows="4" placeholder="请输入回访记录内容" />
                                    </el-form-item>
                                    <el-form-item label="诊疗情况" required>
                                        <el-select v-model="editDraft.treatmentStatusValue" placeholder="请选择诊疗情况" class="followup-select">
                                            <el-option v-for="status in statuses" :key="status.dictValue" :label="status.dictLabel + (status.retired ? '（历史选项）' : '')" :value="status.dictValue" />
                                            <el-option v-if="record.treatmentStatusValue && !statuses.some(status => String(status.dictValue) === String(record.treatmentStatusValue))" :label="statusLabel(record) + '（历史选项）'" :value="record.treatmentStatusValue" disabled />
                                        </el-select>
                                    </el-form-item>
                                    <div class="followup-actions followup-edit-actions">
                                        <el-button type="primary" size="small" :loading="saving" @click="saveEdit(record)">
                                            <el-icon class="followup-action-icon"><Check /></el-icon>保存
                                        </el-button>
                                        <el-button size="small" :disabled="saving" @click="cancelEdit">
                                            <el-icon class="followup-action-icon"><Close /></el-icon>取消
                                        </el-button>
                                    </div>
                                </el-form>
                            </template>
                            <template v-else>
                                <div class="followup-field"><p>{{ record.followupContent }}</p></div>
                                <div class="followup-treatment-status">
                                    <span>诊疗情况：</span>
                                    <el-tag :type="treatmentStatusType(record)" size="small" effect="light">{{ statusLabel(record) }}</el-tag>
                                </div>
                                <!-- 创建及修改信息放在底部，编辑操作保持在右侧。 -->
                                <div class="followup-card-footer">
                                    <div class="followup-meta">
                                        <span>创建人：{{ displayName(record, 'create') }}</span>
                                        <!-- 新建版本为 0；成功编辑后版本递增并记录修改时间。 -->
                                        <template v-if="Number(record.version) > 0 || record.updateTime">
                                            <span>最后修改人：{{ displayName(record, 'update') }}</span>
                                            <span>最后修改时间：{{ formatDateTime(record.updateTime) }}</span>
                                        </template>
                                    </div>
                                    <el-button
                                        type="primary"
                                        size="small"
                                        plain
                                        round
                                        :disabled="isAdding || editingFollowupId !== null || saving"
                                        @click="startEdit(record)"
                                    >
                                        <el-icon class="followup-edit-icon"><Edit /></el-icon>
                                        编辑
                                    </el-button>
                                </div>
                            </template>
                        </div>
                    </div>
                    <!-- 新增草稿始终位于最新记录一侧，切换排序时保留已输入的内容。 -->
                    <div v-if="isAdding" class="followup-entry" :style="{ order: followupSortOrder === 'desc' ? -1 : 1 }">
                        <div class="followup-time">
                            <span class="followup-time-icon" aria-hidden="true"><el-icon><Clock /></el-icon></span>
                            <span>{{ todayDateOnly() }}（未保存）</span>
                        </div>
                        <div class="followup-card">
                            <el-form label-position="top" :model="newDraft">
                                <el-form-item label="回访记录内容" required>
                                    <el-input v-model="newDraft.followupContent" type="textarea" :rows="4" placeholder="请输入回访记录内容" />
                                </el-form-item>
                                <el-form-item label="诊疗情况" required>
                                    <el-select v-model="newDraft.treatmentStatusValue" placeholder="请选择诊疗情况" class="followup-select">
                                        <el-option v-for="status in statuses" :key="status.dictValue" :label="status.dictLabel" :value="status.dictValue" />
                                    </el-select>
                                </el-form-item>
                                <div class="followup-actions followup-edit-actions">
                                    <el-button type="primary" size="small" :loading="saving" @click="saveNew">
                                        <el-icon class="followup-action-icon"><Check /></el-icon>保存
                                    </el-button>
                                    <el-button size="small" :disabled="saving" @click="cancelAdd">
                                        <el-icon class="followup-action-icon"><Close /></el-icon>取消
                                    </el-button>
                                </div>
                            </el-form>
                        </div>
                    </div>
                    </div>
                </template>
            </main>
            <footer class="followup-footer"><el-button @click="requestClose">关闭</el-button></footer>
        </section>
    </el-drawer>
</template>

<script>
import {Check, Clock, Close, CloseBold, Edit, Refresh, Sort} from "@element-plus/icons-vue";
import followup from "@/assets/scripts/components/followup";

export default {
    ...followup,
    components: {Check, Clock, Close, CloseBold, Edit, Refresh, Sort}
};
</script>

<style scoped>
:deep(.followup-drawer .el-drawer__body) { padding: 0; overflow: hidden; }
.followup-layout { height: 100%; display: flex; flex-direction: column; min-height: 0; color: #303133; }
.followup-header { flex: 0 1 auto; min-height: 0; display: flex; flex-direction: column; padding: 14px 22px 0; border-bottom: 1px solid #e4e7ed; }
.followup-heading { flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between; font-size: 17px; padding-bottom: 12px; }
.followup-records-section { margin-bottom: 16px; }
.followup-fixed-toolbar {
    flex: 0 0 auto;
    padding: 0 24px;
    background: var(--el-fill-color-light, #f5f7fa);
    border-bottom: 1px solid var(--el-border-color-lighter, #ebeef5);
    box-shadow: 0 2px 6px rgba(31, 45, 61, 0.08);
    z-index: 10;
}
.followup-records-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    min-height: 44px;
    padding: 8px 0;
}
.followup-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #303133;
    font-size: 15px;
    font-weight: 600;
}
.followup-toolbar-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 10px;
    margin-left: auto;
}
.followup-sort-label {
    margin-right: 6px;
}
.followup-sort-icon {
    display: inline-flex;
    align-items: center;
    margin-left: 2px;
    color: var(--el-color-primary, #409eff);
    transition: transform 0.2s ease;
}
.followup-sort-icon.is-ascending {
    transform: rotate(180deg);
}
.followup-toolbar-actions > .el-button + .el-button {
    margin-left: 0;
}
.followup-empty-state { display: flex; flex-direction: column; align-items: center; padding: 12px 0 14px; border: 1px dashed #dcdfe6; border-radius: 8px; background: #fafafa; }
.followup-empty-state :deep(.el-empty) { padding: 8px 0 12px; }
.followup-empty-state :deep(.el-empty__description) { margin-top: 8px; }
.followup-summary { overflow-y: auto; min-height: 0; max-height: 36vh; padding-bottom: 14px; }
/* 回访抽屉按业务区域独立成块，复用主题色并统一卡片阴影。 */
.followup-layout {
    background: var(--el-fill-color-light, #f5f7fa);
}
.followup-header {
    border-bottom: 0;
}
.followup-summary {
    padding: 3px 4px 16px;
}
.followup-block,
.followup-card,
.followup-empty-state,
.followup-list-actions {
    border: 1px solid var(--el-border-color-lighter, #ebeef5);
    border-radius: 10px;
    box-shadow: 0 3px 12px rgba(31, 45, 61, 0.07);
}
.followup-info-section {
    padding: 14px 16px;
    background: var(--el-color-primary-light-9, #ecf5ff);
}
.followup-info-section + .followup-info-section {
    margin-top: 14px;
    background: var(--el-bg-color, #ffffff);
}
.followup-info-title { padding-left: 10px; border-left: 3px solid #409eff; color: #303133; font-size: 14px; font-weight: 600; line-height: 20px; }
.followup-info-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px 16px; margin-top: 9px; }
.followup-info-grid-wide { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.followup-info-grid > div { min-width: 0; font-size: 13px; line-height: 1.55; }
.followup-info-grid span { color: #909399; }
.followup-info-grid strong { color: #303133; font-weight: 500; }
.followup-info-grid p { display: inline; margin: 0; overflow-wrap: anywhere; white-space: pre-wrap; }
.followup-timeline { flex: 1; overflow-y: auto; min-height: 0; padding: 18px 24px 24px; }
.followup-record-list {
    display: flex;
    flex-direction: column;
}
.followup-entry { position: relative; border-left: 2px solid #dcdfe6; padding: 0 0 20px 17px; }
.followup-entry::before { content: ''; position: absolute; top: 8px; left: -5px; width: 8px; height: 8px; border-radius: 50%; background: var(--el-color-primary-light-5, #a0cfff); }
.followup-time {
    display: flex;
    align-items: center;
    gap: 7px;
    color: var(--el-text-color-regular, #606266);
    font-size: 13px;
    padding-bottom: 7px;
}
.followup-time-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: 1px solid var(--el-color-primary-light-5, #a0cfff);
    border-radius: 50%;
    color: var(--el-color-primary, #409eff);
    background: var(--el-color-primary-light-9, #ecf5ff);
    font-size: 14px;
}
.followup-card { border: 1px solid #e4e7ed; border-radius: 8px; padding: 14px 16px; background: #fff; }
.followup-card :deep(.el-form-item) { margin-bottom: 12px; }
.followup-field { margin: 0 0 12px; font-size: 14px; }
.followup-edit-icon,
.followup-action-icon {
    margin-right: 5px;
}
.followup-field p { margin: 0; overflow-wrap: anywhere; white-space: pre-wrap; line-height: 1.65; }
.followup-treatment-status {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
    color: var(--el-text-color-regular, #606266);
    font-size: 13px;
}
.followup-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-top: 10px;
    border-top: 1px solid var(--el-border-color-lighter, #ebeef5);
}
.followup-card-footer > .el-button { flex-shrink: 0; }
.followup-meta { min-width: 0; display: flex; align-items: center; flex-wrap: wrap; gap: 8px 20px; color: #606266; font-size: 12px; overflow-wrap: anywhere; }
.followup-actions { display: flex; justify-content: flex-end; padding-top: 8px; }
.followup-edit-actions { justify-content: flex-start; }
.followup-list-actions {
    display: flex;
    justify-content: center;
    padding: 16px;
    background: var(--el-bg-color, #ffffff);
}
.followup-card > .el-form {
    padding-top: 12px;
}
.followup-field {
    padding: 12px;
    border-radius: 6px;
    background: var(--el-fill-color-light, #f5f7fa);
}
.followup-select { width: 100%; }
.followup-footer { flex: 0 0 auto; display: flex; justify-content: center; padding: 12px 22px; border-top: 1px solid #e4e7ed; }
@media (max-width: 820px) {
    .followup-info-grid, .followup-info-grid-wide { grid-template-columns: 1fr; }
    .followup-fixed-toolbar { padding: 0 16px; }
    .followup-timeline { padding: 16px 16px 20px; }
    .followup-entry { padding-left: 13px; }
    .followup-card { padding: 12px 13px; }
}
</style>
